import axios from 'axios';
import { backendApiGateway } from '~/client/backend';
import { Company } from '~/types/company';
import { Document } from '~/types/document';
import {
  BookkeepingAccount,
  BookkeepingEntrySource,
  TransactionRegistrationProposal,
} from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { PayrollUpload } from '~/types/payroll';
import { buildJsonAPIQueryParams, jsonAPIFilterBuilder } from '~/utils/jsonapi-query';

interface PayrollUploadUrlResponse {
  url: string;
  filename: string;
}

interface PayrollUploadResponse {
  id: string;
  company_id: string;
  creation_date: string;
  url: string;
}

export const createPayrollUpload = async (
  companyId: Company['id'],
  file: File,
  creationDate: string,
): Promise<PayrollUpload> => {
  const {
    data: { url, filename },
  } = await backendApiGateway.post<PayrollUploadUrlResponse>(
    '/api/v1/payroll/upload-url',
    { company_id: companyId, content_type: file.type },
    { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } },
  );

  await axios.put(url, file, { headers: { 'Content-Type': file.type } });

  const { data } = await backendApiGateway.post<PayrollUploadResponse>(
    '/api/v1/payroll/upload',
    { company_id: companyId, creation_date: creationDate, filename },
    { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } },
  );

  return {
    id: data.id,
    companyId: data.company_id,
    creationDate: data.creation_date,
    url: data.url,
  };
};

interface PayrollRegistrationProposalEntry {
  amount: Money;
  document: Document;
  bookkeepingAccount: BookkeepingAccount;
  source: BookkeepingEntrySource;
}

interface PayrollBookkeepingProposalResponse {
  documentId: string;
  companyId: string;
  date: string;
  entries: PayrollRegistrationProposalEntry[];
}

export const fetchPayrollBookkeepingProposal = async (
  companyId: Company['id'],
  documentId: Document['id'],
): Promise<Omit<TransactionRegistrationProposal, 'description'> | null> => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  filterBuilder.eq('id', documentId);
  const params = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<PayrollBookkeepingProposalResponse[]>('/api/v1/payroll/proposals', {
    params,
  });

  const match = data[0] ?? null;
  if (!match) return null;

  const date = new Date(match.date);

  return {
    date,
    entries: match.entries.map((entry) => ({
      document: entry.document,
      bookkeepingAccount: entry.bookkeepingAccount,
      amount: entry.amount,
      source: entry.source,
    })),
    transactions: [],
    recommendedFlowId: null,
  };
};
